import { useEffect, useState } from "react";
import { X, Download, Share, SquarePlus } from "lucide-react";

const SEEN_KEY = "a2hs_seen_v1";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isMobile() {
  return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export default function AddToHomeScreen() {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState(null); // "android" | "ios"
  const [deferred, setDeferred] = useState(null);
  const [showIosSteps, setShowIosSteps] = useState(false);

  useEffect(() => {
    // Enregistre le service worker (nécessaire à l'installabilité Android)
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
    if (isStandalone() || !isMobile() || localStorage.getItem(SEEN_KEY)) return;

    if (isIOS()) {
      setMode("ios");
      setVisible(true);
      localStorage.setItem(SEEN_KEY, "1");
      return;
    }
    // Android / Chrome : attend l'événement natif d'installation
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("android");
      setVisible(true);
      localStorage.setItem(SEEN_KEY, "1");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setVisible(false);
    setShowIosSteps(false);
  };

  const install = async () => {
    if (mode === "ios") { setShowIosSteps(true); return; }
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* ignore */ }
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="a2hs-banner"
        className="fixed inset-x-3 bottom-3 z-[70] rounded-2xl bg-obsidian text-white shadow-2xl p-3.5 flex items-center gap-3 animate-pop sm:hidden"
      >
        <img src="/logo512.png" alt="" className="w-11 h-11 rounded-xl bg-white/10 object-contain p-1 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight">Ajoute MAIRUNCOACH à ton écran d'accueil</div>
          <div className="text-[12px] text-white/60 mt-0.5">Accès en un tap, comme une vraie app.</div>
        </div>
        <button
          data-testid="a2hs-install"
          onClick={install}
          className="shrink-0 rounded-full bg-sage text-obsidian px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 active:scale-95 transition-transform"
        >
          <Download size={15} /> {mode === "ios" ? "Voir comment" : "Installer"}
        </button>
        <button data-testid="a2hs-dismiss" onClick={dismiss} aria-label="Fermer" className="shrink-0 p-1 text-white/50 hover:text-white">
          <X size={18} />
        </button>
      </div>

      {showIosSteps && (
        <div
          data-testid="a2hs-ios-steps"
          className="fixed inset-0 z-[71] flex items-end justify-center bg-obsidian/50 backdrop-blur-sm sm:hidden"
          onClick={dismiss}
        >
          <div className="w-full bg-white rounded-t-3xl p-6 pb-8 animate-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Ajouter à l'écran d'accueil</h3>
              <button onClick={dismiss} aria-label="Fermer" className="p-1 text-obsidian/40 hover:text-obsidian"><X size={20} /></button>
            </div>
            <p className="mt-1.5 text-sm text-obsidian/60">Deux étapes dans Safari :</p>
            <ol className="mt-5 space-y-4">
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 shrink-0 rounded-full bg-sage/15 text-sage font-semibold flex items-center justify-center">1</span>
                <span className="text-sm text-obsidian/85 flex items-center gap-1.5 flex-wrap">
                  Appuie sur le bouton <span className="inline-flex items-center gap-1 font-medium text-obsidian"><Share size={16} className="text-blue" /> Partager</span> en bas de l'écran.
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 shrink-0 rounded-full bg-sage/15 text-sage font-semibold flex items-center justify-center">2</span>
                <span className="text-sm text-obsidian/85 flex items-center gap-1.5 flex-wrap">
                  Choisis <span className="inline-flex items-center gap-1 font-medium text-obsidian"><SquarePlus size={16} /> Sur l'écran d'accueil</span>, puis Ajouter.
                </span>
              </li>
            </ol>
            <button onClick={dismiss} className="mt-7 w-full rounded-full bg-obsidian text-white py-3.5 text-base font-medium">
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </>
  );
}
